import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { EnergyLabelEpc, isBetterThanRegistered } from '@cfp/shared/energy-label';
import { Badge, Icon } from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import { EnergyLabelTypeDTO, LabelGroupDTO, OwnershipStatusDTO, PortfolioBuildingsRoadmapDTO } from 'csr-asset-app';

@Component({
  selector: 'cfp-report-buildings-legend',
  templateUrl: 'report-buildings-legend.html',
  imports: [EnergyLabelEpc, Icon, Badge, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportBuildingsLegend {
  public readonly portfolioBuildingsRoadmap = input.required<PortfolioBuildingsRoadmapDTO>();
  public readonly translationKeyPrefix = input('portfolioBuildingsRoadmapReport');
  public readonly showPlan = input(false);

  protected readonly labelGroup = LabelGroupDTO.EPC;

  private currentDate = new Date();

  protected readonly hasCalculatedBetterLabels = computed(() => {
    return this.portfolioBuildingsRoadmap()?.buildings.some((buildingData) =>
      isBetterThanRegistered(buildingData.energyLabels?.labelSituationType)
    );
  });

  protected readonly hasExpiredLabels = computed(() =>
    this.portfolioBuildingsRoadmap()?.buildings.some(
      (buildingData) =>
        buildingData.energyLabels?.currentEnergyLabel?.labelType === EnergyLabelTypeDTO.REGISTERED &&
        buildingData.energyLabels?.currentEnergyLabel?.validUntil &&
        new Date(buildingData.energyLabels?.currentEnergyLabel?.validUntil) < this.currentDate
    )
  );

  protected readonly hasAlmostExpiredLabels = computed(() =>
    this.portfolioBuildingsRoadmap()?.buildings.some(
      (buildingData) =>
        buildingData.energyLabels?.currentEnergyLabel?.labelType === EnergyLabelTypeDTO.REGISTERED &&
        buildingData.energyLabels?.currentEnergyLabel?.validUntil &&
        new Date(buildingData.energyLabels?.currentEnergyLabel?.validUntil) <
          new Date(this.currentDate.setMonth(this.currentDate.getMonth() + 6))
    )
  );

  protected readonly hasMonument = computed(() =>
    this.portfolioBuildingsRoadmap()?.buildings?.some((buildingData) => buildingData.building.monument)
  );

  protected readonly hasPlan = computed(
    () =>
      this.showPlan() &&
      this.portfolioBuildingsRoadmap()?.buildings?.some((buildingData) => buildingData.roadmap != null)
  );

  protected readonly hasStatusSellBefore2030 = computed(() =>
    this.portfolioBuildingsRoadmap()?.buildings?.some(
      (buildingData) => buildingData.building.ownershipStatus === OwnershipStatusDTO.PLANSTOSELLBEFORE2030
    )
  );

  protected readonly hasManualOverwrittenValue = computed(() =>
    this.portfolioBuildingsRoadmap()?.buildings?.some(
      (buildingData) =>
        buildingData.energyLabelRequirement?.isUserDefined ||
        buildingData.roadmap?.plannedMeasures.some((plannedMeasures) => plannedMeasures.isUserDefined)
    )
  );

  protected readonly LabelGroupDTO = LabelGroupDTO;

  protected translationKey(suffix: string): string {
    return `${this.translationKeyPrefix()}.${suffix}`;
  }
}
